import React from 'react';

// Responsive Container Component
export interface ResponsiveContainerProps {
    children: React.ReactNode;
    className?: string;
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '7xl';
    padding?: boolean;
}

export const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
    children,
    className = '',
    maxWidth = '7xl',
    padding = true
}) => {
    const maxWidthClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '7xl': 'max-w-7xl'
    };

    const paddingClasses = padding ? 'px-2 sm:px-4 lg:px-8' : '';

    return (
        <div className={`${maxWidthClasses[maxWidth]} mx-auto ${paddingClasses} ${className}`}>
            {children}
        </div>
    );
};

// Responsive Grid Component
export interface ResponsiveGridProps {
    children: React.ReactNode;
    className?: string;
    cols?: {
        default?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
    };
    gap?: number;
}

export const ResponsiveGrid: React.FC<ResponsiveGridProps> = ({
    children,
    className = '',
    cols = { default: 1, md: 2, lg: 3 },
    gap = 6
}) => {
    const gridClasses = [
        `grid gap-${gap}`,
        cols.default && `grid-cols-${cols.default}`,
        cols.sm && `sm:grid-cols-${cols.sm}`,
        cols.md && `md:grid-cols-${cols.md}`,
        cols.lg && `lg:grid-cols-${cols.lg}`,
        cols.xl && `xl:grid-cols-${cols.xl}`
    ].filter(Boolean).join(' ');

    return (
        <div className={`${gridClasses} ${className}`}>
            {children}
        </div>
    );
};

// Responsive Stack Component
export interface ResponsiveStackProps {
    children: React.ReactNode;
    className?: string;
    direction?: 'horizontal' | 'vertical' | 'responsive';
    spacing?: number;
    align?: 'start' | 'center' | 'end' | 'stretch';
    justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
}

export const ResponsiveStack: React.FC<ResponsiveStackProps> = ({
    children,
    className = '',
    direction = 'vertical',
    spacing = 4,
    align = 'stretch',
    justify = 'start'
}) => {
    const directionClasses = {
        horizontal: 'flex flex-row',
        vertical: 'flex flex-col',
        responsive: 'flex flex-col sm:flex-row'
    };

    const alignClasses = {
        start: 'items-start',
        center: 'items-center',
        end: 'items-end',
        stretch: 'items-stretch'
    };

    const justifyClasses = {
        start: 'justify-start',
        center: 'justify-center',
        end: 'justify-end',
        between: 'justify-between',
        around: 'justify-around',
        evenly: 'justify-evenly'
    };

    const spacingClass = direction === 'horizontal' || direction === 'responsive'
        ? `space-x-${spacing} sm:space-x-${spacing} space-y-0 sm:space-y-0`
        : `space-y-${spacing}`;

    return (
        <div className={`
            ${directionClasses[direction]} 
            ${alignClasses[align]} 
            ${justifyClasses[justify]} 
            ${spacingClass}
            ${className}
        `}>
            {children}
        </div>
    );
};

// Mobile-First Show/Hide Components
export const ShowOnMobile: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className = ''
}) => (
    <div className={`sm:hidden ${className}`}>
        {children}
    </div>
);

export const HideOnMobile: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className = ''
}) => (
    <div className={`hidden sm:block ${className}`}>
        {children}
    </div>
);

export const ShowOnTablet: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className = ''
}) => (
    <div className={`hidden sm:block lg:hidden ${className}`}>
        {children}
    </div>
);

export const ShowOnDesktop: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className = ''
}) => (
    <div className={`hidden lg:block ${className}`}>
        {children}
    </div>
);

// Responsive Table Wrapper
export interface ResponsiveTableProps {
    children: React.ReactNode;
    className?: string;
}

export const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
    children,
    className = ''
}) => (
    <div className={`bg-white shadow-sm rounded-lg overflow-hidden ${className}`}>
        <div className="overflow-x-auto">
            {children}
        </div>
    </div>
);
